import { getReceiptFingerprint } from '@/lib/receipts';
import { supabase } from '@/lib/supabase';
import type {
  LegacyQuoteCard,
  PermanentReceipt,
  ReceiptEventType,
  ReceiptLibraryItem,
} from '@/types';

interface ReceiptRow {
  id: string;
  owner_id: string | null;
  source_card_id: string;
  search_query: string;
  card_json: unknown;
  content_hash: string;
  hash_version: number;
  is_public: boolean;
  retrieved_at: string;
  created_at: string;
}

interface ReceiptEventRow {
  event_type: string;
}

interface ReceiptLibraryRow extends ReceiptRow {
  receipt_events: ReceiptEventRow[] | null;
}

const RECEIPT_COLUMNS =
  'id, owner_id, source_card_id, search_query, card_json, content_hash, hash_version, is_public, retrieved_at, created_at';

function isLegacyQuoteCard(value: unknown): value is LegacyQuoteCard {
  if (!value || typeof value !== 'object') return false;
  const card = value as Partial<LegacyQuoteCard>;
  return (
    typeof card.id === 'string' &&
    typeof card.quote === 'string' &&
    typeof card.source === 'string' &&
    typeof card.documentTitle === 'string' &&
    typeof card.date === 'string' &&
    typeof card.canonicalLink === 'string' &&
    (card.lane === 'A' || card.lane === 'B' || card.lane === 'C') &&
    typeof card.laneLabel === 'string'
  );
}

function mapReceiptRow(row: ReceiptRow): PermanentReceipt | null {
  if (!isLegacyQuoteCard(row.card_json) || row.hash_version !== 1) return null;

  return {
    id: row.id,
    ownerId: row.owner_id,
    sourceCardId: row.source_card_id,
    searchQuery: row.search_query,
    card: row.card_json,
    contentHash: row.content_hash,
    hashVersion: 1,
    isPublic: row.is_public,
    retrievedAt: row.retrieved_at,
    createdAt: row.created_at,
  };
}

function mapReceiptLibraryRow(row: ReceiptLibraryRow): ReceiptLibraryItem | null {
  const receipt = mapReceiptRow(row);
  if (!receipt) return null;

  const analytics = {
    created: 0,
    opened: 0,
    shared: 0,
    sourceClicked: 0,
  };

  for (const event of row.receipt_events ?? []) {
    if (event.event_type === 'created') analytics.created += 1;
    if (event.event_type === 'opened') analytics.opened += 1;
    if (event.event_type === 'shared') analytics.shared += 1;
    if (event.event_type === 'source_clicked') analytics.sourceClicked += 1;
  }

  return { ...receipt, analytics };
}

export async function createPermanentReceipt(
  card: LegacyQuoteCard,
  searchQuery?: string,
  requestedOwnerId?: string | null
): Promise<PermanentReceipt> {
  if (!supabase) throw new Error('Permanent receipt storage is not configured');

  const normalizedQuery = searchQuery?.trim() ?? '';
  const contentHash = await getReceiptFingerprint(card, normalizedQuery);
  const {
    data: { user },
  } = await supabase.auth.getUser();
  const ownerId = requestedOwnerId ?? user?.id ?? null;

  let existingQuery = supabase
    .from('receipts')
    .select(RECEIPT_COLUMNS)
    .eq('content_hash', contentHash)
    .eq('is_public', true)
    .limit(1);

  existingQuery = ownerId
    ? existingQuery.eq('owner_id', ownerId)
    : existingQuery.is('owner_id', null);

  const { data: existingData, error: existingError } = await existingQuery.maybeSingle();
  if (existingError) throw new Error(existingError.message);

  if (existingData) {
    const existingReceipt = mapReceiptRow(existingData as ReceiptRow);
    if (existingReceipt) return existingReceipt;
  }

  const { data, error } = await supabase
    .from('receipts')
    .insert({
      owner_id: ownerId,
      source_card_id: card.id,
      search_query: normalizedQuery,
      card_json: card,
      content_hash: contentHash,
      hash_version: 1,
      is_public: true,
    })
    .select(RECEIPT_COLUMNS)
    .single();

  if (error || !data) throw new Error(error?.message ?? 'Unable to create permanent receipt');

  const receipt = mapReceiptRow(data as ReceiptRow);
  if (!receipt) throw new Error('Permanent receipt returned an invalid snapshot');
  return receipt;
}

export async function getPermanentReceipt(receiptId: string): Promise<PermanentReceipt | null> {
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('receipts')
    .select(RECEIPT_COLUMNS)
    .eq('id', receiptId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  return data ? mapReceiptRow(data as ReceiptRow) : null;
}

export async function listOwnedPermanentReceipts(ownerId: string): Promise<ReceiptLibraryItem[]> {
  if (!supabase) throw new Error('Permanent receipt storage is not configured');

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user || user.id !== ownerId) throw new Error('Sign in to view your saved receipts');

  const { data, error } = await supabase
    .from('receipts')
    .select(`${RECEIPT_COLUMNS}, receipt_events(event_type)`)
    .eq('owner_id', ownerId)
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);

  return (data ?? [])
    .map((row) => mapReceiptLibraryRow(row as ReceiptLibraryRow))
    .filter((receipt): receipt is ReceiptLibraryItem => receipt !== null);
}

export async function deletePermanentReceipt(receiptId: string): Promise<void> {
  if (!supabase) throw new Error('Permanent receipt storage is not configured');

  const { error } = await supabase.from('receipts').delete().eq('id', receiptId);
  if (error) throw new Error(error.message);
}

function getReferrerOrigin(): string | null {
  if (typeof document === 'undefined' || !document.referrer) return null;
  try {
    return new URL(document.referrer).origin;
  } catch {
    return null;
  }
}

export async function trackReceiptEvent(
  receiptId: string,
  eventType: ReceiptEventType,
  metadata: Record<string, string> = {}
): Promise<void> {
  if (!supabase) return;

  const { error } = await supabase.from('receipt_events').insert({
    receipt_id: receiptId,
    event_type: eventType,
    referrer_origin: getReferrerOrigin(),
    metadata,
  });

  if (error) console.warn('[Receipt analytics] Event was not recorded:', error.message);
}
